import { createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import {
  BASE_URL,
  getToken,
  getCurrentLanguage,
} from "../../shared/utils.js/utils";
import { makeOfflineRequest } from "../../shared/utils.js/offlineRequestManager";

// Get Exchange Rates
export const getExchangeRates = createAsyncThunk(
  "ExchangeRate/getExchangeRates",
  async (params = {}, { rejectWithValue }) => {
    try {
      const token = getToken();
      const currentLang = getCurrentLanguage();
      if (!token) throw new Error("No authentication token found");
      const queryParams = new URLSearchParams();
      if (params.page) queryParams.append("page", params.page);
      if (params.per_page) queryParams.append("per_page", params.per_page);
      const url = `${BASE_URL}/exchange-rates${queryParams.toString() ? `?${queryParams.toString()}` : ""}`;
      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Accept-Language": currentLang,
        },
      });
      return response.data;
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to get exchange rates!";
      return rejectWithValue(message);
    }
  },
);

// Get Exchange Rate Details
export const getExchangeRateDetails = createAsyncThunk(
  "ExchangeRate/getExchangeRateDetails",
  async (id, { rejectWithValue }) => {
    try {
      const token = getToken();
      const currentLang = getCurrentLanguage();
      if (!token) throw new Error("No authentication token found");
      const response = await axios.get(`${BASE_URL}/exchange-rates/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Accept-Language": currentLang,
        },
      });
      return response.data;
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to get exchange rate details!";
      return rejectWithValue(message);
    }
  },
);

// Get Effective Exchange Rate
export const getEffectiveExchangeRate = createAsyncThunk(
  "ExchangeRate/getEffectiveExchangeRate",
  async ({ currency_id, date }, { rejectWithValue }) => {
    try {
      const token = getToken();
      const currentLang = getCurrentLanguage();
      if (!token) throw new Error("No authentication token found");
      const queryParams = new URLSearchParams();
      queryParams.append("currency_id", currency_id);
      queryParams.append("date", date);
      const response = await axios.get(
        `${BASE_URL}/exchange-rates/effective?${queryParams.toString()}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Accept-Language": currentLang,
          },
        },
      );
      return response.data;
    } catch (error) {
      const message =
        error.response?.data?.message ||
        "Failed to get effective exchange rate!";
      return rejectWithValue(message);
    }
  },
);

// Add Exchange Rate
export const addExchangeRate = createAsyncThunk(
  "ExchangeRate/addExchangeRate",
  async (data, { rejectWithValue }) => {
    try {
      // Use makeOfflineRequest for offline POST support
      const response = await makeOfflineRequest(
        `/exchange-rates`,
        "POST",
        data,
      );
      return response;
    } catch (error) {
      // If error is from offline queue, treat as optimistic update
      if (error.message === "OFFLINE_REQUEST_SAVED" || error.isOffline) {
        return {
          success: true,
          message: "Saved offline, will sync when online.",
        };
      }
      let message =
        error.response?.message ||
        error.message ||
        "Failed to add exchange rate!";
      if (error.response?.errors) {
        message = JSON.stringify(error.response.errors);
      }
      return rejectWithValue(message);
    }
  },
);

// Delete Exchange Rate
export const deleteExchangeRate = createAsyncThunk(
  "ExchangeRate/deleteExchangeRate",
  async (id, { rejectWithValue }) => {
    try {
      const token = getToken();
      const currentLang = getCurrentLanguage();
      if (!token) throw new Error("No authentication token found");
      const response = await axios.delete(`${BASE_URL}/exchange-rates/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Accept-Language": currentLang,
        },
      });
      return response.data;
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to delete exchange rate!";
      return rejectWithValue(message);
    }
  },
);
